/**
 * Mobile wallet utilities for deep linking, platform detection, and app store URLs.
 * Implements industry-standard patterns for mobile wallet connections.
 */

// Nordex App Links — sourced from Doppler env vars with fallbacks
export const NORDEX_APP_LINKS = {
  ios: import.meta.env.VITE_IOS_APP_URL || "https://testflight.apple.com/join/VnQeB4AZ",
  android: import.meta.env.VITE_GOOGLE_PLAY_URL || import.meta.env.VITE_APK_DOWNLOAD_URL || "https://play.google.com/store/apps/details?id=com.nordex.app",
} as const;

// Wallet configuration with deep links and store URLs
export const WALLET_CONFIG = {
  phantom: {
    name: "Phantom",
    // Universal link format for Phantom mobile
    getDeepLink: (url: string) =>
      `https://phantom.app/ul/browse/${encodeURIComponent(url)}`,
    ios: "https://apps.apple.com/app/phantom-solana-wallet/id1598432977",
    android: "https://play.google.com/store/apps/details?id=app.phantom",
  },
  solflare: {
    name: "Solflare",
    // Universal link format for Solflare mobile
    getDeepLink: (url: string) =>
      `https://solflare.com/ul/v1/browse/${encodeURIComponent(url)}`,
    ios: "https://apps.apple.com/app/solflare/id1580902717",
    android: "https://play.google.com/store/apps/details?id=com.solflare.mobile",
  },
} as const;

export type WalletKey = keyof typeof WALLET_CONFIG;
export type Platform = "ios" | "android" | "desktop";

/**
 * Detect the user's platform based on user agent
 */
export const getPlatform = (): Platform => {
  if (typeof navigator === "undefined") return "desktop";

  const ua = navigator.userAgent;

  if (/iPhone|iPad|iPod/.test(ua)) return "ios";
  if (/Android/.test(ua)) return "android";

  return "desktop";
};

/**
 * Check if the current device is mobile (iOS or Android)
 */
export const isMobileDevice = (): boolean => {
  const platform = getPlatform();
  return platform === "ios" || platform === "android";
};

/**
 * Get the appropriate app store URL for a wallet based on platform
 */
export const getWalletStoreUrl = (
  wallet: WalletKey,
  platform?: Platform
): string => {
  const p = platform || getPlatform();
  const config = WALLET_CONFIG[wallet];

  if (p === "ios") return config.ios;
  if (p === "android") return config.android;

  // Default to iOS for desktop (they shouldn't see this anyway)
  return config.ios;
};

/**
 * Get the Nordex app download URL for the current platform
 * Returns null if no app is available for the platform
 */
export const getNordexAppUrl = (platform?: Platform): string | null => {
  const p = platform || getPlatform();

  if (p === "ios") return NORDEX_APP_LINKS.ios;
  if (p === "android") return NORDEX_APP_LINKS.android;

  return null;
};

/**
 * Check if the Nordex app is available for the current platform
 */
export const isNordexAppAvailable = (platform?: Platform): boolean => {
  return getNordexAppUrl(platform) !== null;
};

/**
 * Attempt to open a wallet app via deep link.
 * Uses visibility change detection to determine if the app opened.
 *
 * @param wallet - The wallet to open (phantom or solflare)
 * @param timeoutMs - How long to wait before assuming app isn't installed (default 1500ms)
 * @returns Promise that resolves to 'opened' if app opened, 'not_installed' if timeout
 */
export const openWalletApp = (
  wallet: WalletKey,
  timeoutMs = 1500
): Promise<"opened" | "not_installed"> => {
  return new Promise((resolve) => {
    const config = WALLET_CONFIG[wallet];
    const currentUrl = window.location.href;
    const deepLink = config.getDeepLink(currentUrl);

    let resolved = false;
    const startTime = Date.now();

    // Handler for visibility change (app opened and we're now in background)
    const handleVisibilityChange = () => {
      if (document.hidden && !resolved) {
        resolved = true;
        cleanup();
        resolve("opened");
      }
    };

    // Handler for blur (alternative detection method)
    const handleBlur = () => {
      // Only count blur if it happens quickly after navigation attempt
      if (Date.now() - startTime < timeoutMs && !resolved) {
        resolved = true;
        cleanup();
        resolve("opened");
      }
    };

    const cleanup = () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleBlur);
    };

    // Set up listeners
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleBlur);

    // Attempt to open the wallet app
    window.location.href = deepLink;

    // Timeout - if we're still here, app probably isn't installed
    setTimeout(() => {
      if (!resolved) {
        resolved = true;
        cleanup();
        resolve("not_installed");
      }
    }, timeoutMs);
  });
};

/**
 * Open the app store for a wallet
 */
export const openWalletStore = (wallet: WalletKey): void => {
  const url = getWalletStoreUrl(wallet);
  window.open(url, "_blank", "noopener,noreferrer");
};

/**
 * Open the Nordex app download page (TestFlight for iOS)
 */
export const openNordexApp = (): void => {
  const url = getNordexAppUrl();
  if (url) {
    window.open(url, "_blank", "noopener,noreferrer");
  }
};

// LocalStorage key for banner dismissal
const BANNER_DISMISSED_KEY = "nordex-mobile-app-banner-dismissed";

/**
 * Check if the mobile app banner has been dismissed
 */
export const isMobileAppBannerDismissed = (): boolean => {
  if (typeof localStorage === "undefined") return false;
  return localStorage.getItem(BANNER_DISMISSED_KEY) === "true";
};

/**
 * Dismiss the mobile app banner (persists to localStorage)
 */
export const dismissMobileAppBanner = (): void => {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(BANNER_DISMISSED_KEY, "true");
};

/**
 * Reset the mobile app banner dismissal (for testing)
 */
export const resetMobileAppBannerDismissal = (): void => {
  if (typeof localStorage === "undefined") return;
  localStorage.removeItem(BANNER_DISMISSED_KEY);
};
